import {Component, Injector, OnInit} from "@angular/core";
import {DtoComponent, createDtoProperties, SubmitBatchEvent, CudBatchItem, SubmitBatchResult, SubmitEvent} from "@mango/core";
import {Visitor} from "../visitor";
import {VisitorService} from "../visitor.service";
import {CompanyService} from "../company.service";
import {SchoolService} from "../school.service";
import {School} from "../school";
import {Company} from "../company";
import {ActivatedRoute} from "@angular/router";

@Component({
  selector: "app-visitor",
  templateUrl: "./visitor.component.html",
  styleUrls: ["./visitor.component.css"],
})
export class VisitorComponent extends DtoComponent<Visitor> implements OnInit {
  totalDuration: number = null;
  visitorSchool: string;
  selectedOption: string;

  properties = createDtoProperties<Visitor>([
    "name",
    {company: ["id", "name"]},
    {school: ["id", "name"]},
    "other",
    "meetingPerson",
    "registerTime",
    "unregisterTime",
    "totalDurationMin",
  ]);

  constructor(
    private visitorService: VisitorService,
    private companyService: CompanyService,
    private schoolService: SchoolService,
    private route: ActivatedRoute,
    injector: Injector
  ) {
    super(injector, visitorService, Visitor);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.handleRoute("id").subscribe(() => {
      const visitor = this.form.getRawValue() as Visitor;
      if (visitor.company) {
        this.selectedOption = "company";
        this.form.controls.company.setValue(visitor.company.name);
      } else if (visitor.school) {
        this.selectedOption = "school";
        this.form.controls.school.setValue(visitor.school.name);
      } else if (visitor.other) {
        this.selectedOption = "other";
        this.form.controls.other.setValue(visitor.other);
      } else {
        this.selectedOption = "company";
      }

      this.totalDuration = visitor.totalDurationMin;

      this.form.controls.unregisterTime.valueChanges.subscribe(
        (unregisterTime) => {
          this.onUnregisterTimeChange(new Date(unregisterTime));
        }
      );
    });
  }

  setOption(option: string): void {
    this.selectedOption = option;
  }

  onUnregisterTimeChange(unregisterTime: Date) {
    if (unregisterTime.getTime() !== 0) {
      const registerTime = new Date(this.form.get("registerTime")?.value);
      const differenceInMilliseconds = unregisterTime.getTime() - registerTime.getTime();
      const differenceInMinutes = Math.floor(differenceInMilliseconds / (1000 * 60));
      this.form.get("totalDurationMin")?.setValue(differenceInMinutes);
      this.form.get("totalDurationMin").markAsDirty();
      this.form.get("totalDurationMin").markAsTouched();
    }
  }

  private async createEntity(entityName: string): Promise<{ name: string; id: number }> {
    if (this.selectedOption === "other") {
      return {name: entityName, id: null};
    }

    const entityService = this.selectedOption === "company" ? this.companyService : this.schoolService;
    const EntityClass = this.selectedOption === "company" ? Company : School;
    const newEntity = new EntityClass();
    newEntity.name = entityName;
    const existingEntities = await entityService.get(["name"]).toPromise();
    const existingEntity = existingEntities.data.find((entity) => entity.name === entityName);
    if (existingEntity) {
      return {name: entityName, id: existingEntity.id};
    }

    const createdEntity = await entityService.create(newEntity).toPromise();
    return {name: entityName, id: createdEntity.id};
  }

  async onSubmitBatch(event: SubmitBatchEvent, handleBatch: (batch: CudBatchItem[]) => Promise<SubmitBatchResult>): Promise<void> {
    const updatedGuest = this.form.getRawValue() as Visitor;
    const entityName = this.form.controls[this.selectedOption].value;
    const id = Number(this.route.snapshot.paramMap.get("id"));

    this.form.controls.school.setValue(null);
    this.form.controls.company.setValue(null);
    this.form.controls.other.setValue(null);
    updatedGuest.school = null;
    updatedGuest.company = null;
    updatedGuest.other = null;

    if (this.selectedOption === "company") {
      updatedGuest.other = null;
    } else if (this.selectedOption === "school") {
      updatedGuest.company = null;
    } else if (this.selectedOption === "other") {
      updatedGuest.other = entityName;
    }

    try {
      const entity = await this.createEntity(entityName);
      if (this.selectedOption !== "other") {
        this.form.controls[this.selectedOption].setValue(entity.name);
        updatedGuest[this.selectedOption] = {id: entity.id, name: entity.name};
      } else {
        this.form.controls.other.setValue(entity.name);
        updatedGuest.other = entity.name;
      }

      updatedGuest.id = id;
      await this.visitorService.update(updatedGuest).toPromise();
    } catch (error) {
      await this.dialogs.alert("Fout", "Er is een fout opgetreden, probeer het later nog een keer.", "warning");
    }

    return super.onSubmitBatch(event, handleBatch);
  }
}
